import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Drawer, Tabs } from "antd";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import {
  updateOpenModalControlPanelLift,
  updateLiftSelected,
  updateBankSelected,
} from "../../../redux/monitoring/monitoringSlice";
import { useLazyGetServerQuery } from "../../../redux/servers/serverAPI";
// COMPONENTS
import ControlPanelLiftHeader from "./ControlPanelLiftHeader";
import ControlPanelLiftFaults from "./ControlPanelLiftFaults";
import ControlPanelLiftStatus from "./ControlPanelLiftStatus";
import ControlPanelLiftActions from "./ControlPanelLiftActions";
import ControlPanelStateView from "./ControlPanelStateView";
import ButtonClose from "../../components/buttons/ButtonClose";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import GETJwtToken from "../../../redux/authentication/authentication";
// CONSTANTS
import { BLANK_LIFT } from "../../../constants/lifts";
import { BLANK_BANK } from "../../../constants/bank";
import { BLANK_SERVER } from "../../../constants/server";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
// UTILS
import { uuidToString, ZERO_UUID_STRING } from "../../../utils/uuid";
import formatUuidT from "../../../flatbuffers/display/uuid";
// SCOPE
import {
  useCanOpenControlPanel,
  useEffectivePermission,
} from "../../../customHooks/useScopedPermission";
import { PermissionAction } from "../../../flatbuffers/generated/archive/permission-action";

export default function ControlPanelLift() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const {
    liftSelected,
    bankSelected,
    openModalControlPanelLift,
    controlPanelTab,
    controlPanelDrawerPlacement,
    serverId,
  } = useSelector((state: RootState) => state.monitoring);
  const { permissionSet } = useSelector((state: RootState) => state.user);

  // ************************************************ */
  // SCOPE CHECK ************************************ */
  // Gate the Actions tab: the operator must have feature permission AND
  // the per-bank/lift ACTION scope grant (empty rows ⇒ allow-all).
  // Permission re-exports from types/permissions so the string value matches.
  // formatUuidT keys must match those built by extractRoleScopeGrants: it passes
  // a string id through unchanged and stringifies a UuidT to its hyphenated form
  // (String() would yield "[object Object]" for a UuidT and never match the map).
  const bankIdStr = formatUuidT(bankSelected.id);
  const liftIdStr = formatUuidT(liftSelected.id);
  // No tab would render ⇒ the drawer must not open, whatever set the redux flag.
  const canOpenPanel = useCanOpenControlPanel("lift");
  const actionsAllowed = useEffectivePermission(
    Permission.MonitorControlPanelActions,
    PermissionAction.ACTION,
    { bankId: bankIdStr, liftId: liftIdStr },
  );

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [tabActiveKey, setTabActiveKey] = useState<string>("FAULTS");
  const [server, setServer] = useState<ServerInterface>({ ...BLANK_SERVER });

  const adapterIdStr = useMemo(() => {
    const id = uuidToString(bankSelected.adapterId);
    return id === ZERO_UUID_STRING ? "" : id;
  }, [bankSelected.adapterId]);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [triggerGetServer] = useLazyGetServerQuery();

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const getServer = async () => {
    const token = await GETJwtToken();
    try {
      const result = await triggerGetServer({
        token,
        idServer: serverId,
      }).unwrap();
      if (result && result.data) {
        setServer(result.data);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const onClickClose = () => {
    dispatch(updateOpenModalControlPanelLift(false));
    dispatch(updateLiftSelected({ ...BLANK_LIFT }));
    dispatch(updateBankSelected({ ...BLANK_BANK }));
  };

  // ************************************************* */
  // USE EFFECT ************************************** */
  useEffect(() => {
    setTabActiveKey(controlPanelTab.tab);
  }, [controlPanelTab]);

  useEffect(() => {
    if (openModalControlPanelLift) {
      getServer();
    }
  }, [openModalControlPanelLift]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <Drawer
      width={850}
      placement={controlPanelDrawerPlacement}
      closable={false}
      onClose={onClickClose}
      open={openModalControlPanelLift && canOpenPanel}
      push={false}
      footer={
        <div style={{ textAlign: "right" }}>
          <ButtonClose onClick={onClickClose} />
        </div>
      }
    >
      <div>
        {/** ************************************************** */}
        {/* HEADER */}
        <div>
          <ControlPanelLiftHeader server={server} />
        </div>
        <br />
        <div>
          {/** ************************************************** */}
          {/* BODY */}
          <Tabs
            onTabClick={(key: string) => {
              setTabActiveKey(key);
            }}
            activeKey={tabActiveKey}
            items={[
              ...(actionsAllowed
                ? [
                    {
                      key: "ACTIONS",
                      label: "Actions",
                      children: (
                        <ControlPanelLiftActions
                          isControlPanel
                          bankId={String(bankSelected.id ?? "")}
                          bankName={String(bankSelected.name ?? "")}
                        />
                      ),
                    },
                  ]
                : []),
              ...(hasPermission(Permission.MonitorControlPanelFaults, permissionSet ?? [])
                ? [
                    {
                      key: "FAULTS",
                      label: "Faults",
                      children: <ControlPanelLiftFaults />,
                    },
                  ]
                : []),
              ...(controlPanelTab.liftType === LiftType[LiftType.ELEVATOR] &&
              hasPermission(Permission.MonitorControlPanelStatus, permissionSet ?? [])
                ? [
                    {
                      key: "STATUS",
                      label: "Status",
                      children: <ControlPanelLiftStatus bank={bankSelected} />,
                    },
                  ]
                : []),
              ...(hasPermission(Permission.MonitorControlPanelState, permissionSet ?? [])
                ? [
                    {
                      key: "STATE",
                      label: "State",
                      children: (
                        <ControlPanelStateView
                          liftId={String(liftSelected.id ?? "")}
                          bankId={String(bankSelected.id ?? "")}
                          adapterId={adapterIdStr}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </div>
      </div>
    </Drawer>
  );
}
